'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import keycloak from '../lib/keycloak';
import { api, getBaseApiUrl } from '../lib/api';

interface AuthContextType {
  authenticated: boolean;
  token: string | null;
  user: any | null;
  login: () => void;
  register: () => void;
  logout: () => void;
  setAuthSession: (token: string, user: any, refreshToken?: string) => void;
}

const AuthContext = createContext<AuthContextType>({
  authenticated: false,
  token: null,
  user: null,
  login: () => {},
  register: () => {},
  logout: () => {},
  setAuthSession: () => {},
});

export const KeycloakProvider = ({ children }: { children: React.ReactNode }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const setAuthSession = (newToken: string, newUser: any, refreshToken?: string) => {
    setToken(newToken);
    setUser(newUser);
    setAuthenticated(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('scalesutra_token', newToken);
      localStorage.setItem('scalesutra_user', JSON.stringify(newUser));
      if (refreshToken) {
        localStorage.setItem('scalesutra_refresh_token', refreshToken);
      } else {
        localStorage.removeItem('scalesutra_refresh_token');
      }
    }
  };

  useEffect(() => {
    // The Connect client has its own HTTP-only customer session. Do not redirect
    // it through admin SSO or silently reuse a different Board identity.
    if (typeof window !== 'undefined' && window.location.pathname === '/connect') {
      setLoading(false);
      return;
    }
    // Check local storage session first
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('scalesutra_token');
      const storedUser = localStorage.getItem('scalesutra_user');
      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setAuthenticated(true);
          setLoading(false);

          // Silent background re-verification with /auth/me (triggers refresh interceptor if expired)
          api.get('/auth/me')
            .then((res) => {
              if (res.data?.data) {
                setUser(res.data.data);
                localStorage.setItem('scalesutra_user', JSON.stringify(res.data.data));
              }
            })
            .catch((err) => {
              console.warn('[KeycloakProvider] Session refresh check:', err?.message);
            });
          return;
        } catch (e) {
          localStorage.removeItem('scalesutra_token');
          localStorage.removeItem('scalesutra_user');
          localStorage.removeItem('scalesutra_refresh_token');
        }
      }
    }

    if (!keycloak) {
      setLoading(false);
      return;
    }

    keycloak
      .init({
        onLoad: 'check-sso',
        checkLoginIframe: false,
        pkceMethod: 'S256',
      })
      .then((auth) => {
        setAuthenticated(auth);
        if (auth && keycloak?.token) {
          setToken(keycloak.token);
          keycloak.loadUserProfile().then((profile) => {
            setUser(profile);
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Keycloak Init Error:', err);
        setLoading(false);
      });
  }, []);

  // Proactive Silent Auto-Refresh every 4 minutes to keep web session active continuously
  useEffect(() => {
    if (!authenticated) return;

    const silentRefresh = async () => {
      if (typeof window === 'undefined') return;
      const refreshToken = localStorage.getItem('scalesutra_refresh_token');
      if (refreshToken) {
        try {
          const refreshUrl = `${getBaseApiUrl()}/auth/refresh`;
          const res = await axios.post(refreshUrl, { refresh_token: refreshToken });
          if (res.data?.data?.access_token) {
            const newAccessToken = res.data.data.access_token;
            const newRefreshToken = res.data.data.refresh_token || res.data.data.refreshToken || refreshToken;

            setToken(newAccessToken);
            localStorage.setItem('scalesutra_token', newAccessToken);
            localStorage.setItem('scalesutra_refresh_token', newRefreshToken);
          }
        } catch (err) {
          console.warn('[Web KeycloakProvider] Silent auto-refresh warning:', err);
        }
      }
    };

    const interval = setInterval(silentRefresh, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authenticated]);

  const login = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  };

  const register = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/signup';
    }
  };

  const logout = async () => {
    if(token?.startsWith('sswa1_'))await api.post('/auth/whatsapp/logout',{}, {timeout:5000}).catch(()=>{});
    setToken(null);
    setUser(null);
    setAuthenticated(false);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('scalesutra_token');
      localStorage.removeItem('scalesutra_user');
      localStorage.removeItem('scalesutra_refresh_token');
      window.location.href = '/login';
    }
    if (keycloak && keycloak.authenticated) {
      keycloak.logout();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950 text-white font-sans">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 text-sm font-medium font-sans">Loading ScaleSutra Workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ authenticated, token, user, login, register, logout, setAuthSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
